import { useState, useEffect } from 'react';
import { incidentsApiService } from '../services/incidentsApi';
import { notesApiService } from '../services/notesApi';
import { X, Loader, Search, AlertCircle, FileText, TriangleAlert, Axe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDebounce } from '../hooks/useDebounce';
import { AssociatedItem } from '../types/player';
import { EmptyState } from './ui/EmptyState';
import { SkeletonLoader } from './SkeletonLoader';

interface LinkItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  incidentId: string;
}

export const LinkItemModal = ({ isOpen, onClose, onSuccess, incidentId }: LinkItemModalProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [isLinking, setIsLinking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<AssociatedItem[]>([]);
  const [selectedType, setSelectedType] = useState<'all' | 'note' | 'warning' | 'ban'>('all');

  const debouncedSearch = useDebounce(searchQuery, 300);

  useEffect(() => {
    if (isOpen && debouncedSearch.length >= 2) {
      searchItems();
    } else if (isOpen) {
      setResults([]);
    }
  }, [isOpen, debouncedSearch, selectedType]);

  const searchItems = async () => {
    if (!debouncedSearch || debouncedSearch.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const searchResults: AssociatedItem[] = [];

      // Search notes and warnings
      if (selectedType === 'all' || selectedType === 'note' || selectedType === 'warning') {
        try {
          const typeFilter = selectedType === 'all' ? 'all' : selectedType;
          const data = await notesApiService.getAllEntries(1, 50, typeFilter as 'note' | 'warning' | 'all');

          data.results.forEach((item: any) => {
            if (item.content?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
                item.id?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
                item.player_name?.toLowerCase().includes(debouncedSearch.toLowerCase())) {
              searchResults.push({
                id: item.id,
                type: item.type,
                content: item.content || '',
                creator: item.creator || '',
                created_at: item.created_at,
                user_uuid: item.uuid || '',
                discord_id: item.discord_id || null,
              });
            }
          });
        } catch (err) {
          console.error('Failed to search notes/warnings:', err);
        }
      }

      // Search bans
      if (selectedType === 'all' || selectedType === 'ban') {
        try {
          const bansData = await notesApiService.getAllBans(1, 50);
          bansData.results.forEach((ban: any) => {
            if (ban.reason?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
                ban.ban_uuid?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
                ban.player_name?.toLowerCase().includes(debouncedSearch.toLowerCase())) {
              searchResults.push({
                id: ban.ban_uuid,
                type: 'ban',
                content: ban.reason || '',
                creator: ban.banner_email || '',
                created_at: ban.created || 0,
                user_uuid: ban.user_uuid || '',
                discord_id: null,
              });
            }
          });
        } catch (err) {
          console.error('Failed to search bans:', err);
        }
      }

      setResults(searchResults);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to search items');
    } finally {
      setLoading(false);
    }
  };

  const handleLink = async (itemId: string, itemType: 'note' | 'warning' | 'ban') => {
    setIsLinking(itemId);
    setError(null);

    try {
      await incidentsApiService.linkItem(incidentId, itemId, itemType);
      onSuccess();
      onClose();
      setSearchQuery('');
      setResults([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link item');
    } finally {
      setIsLinking(null);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'note':
        return <span className="icon-tile icon-tile-sm icon-tile-info"><FileText className="w-3.5 h-3.5" /></span>;
      case 'warning':
        return <span className="icon-tile icon-tile-sm icon-tile-warning"><TriangleAlert className="w-3.5 h-3.5" /></span>;
      case 'ban':
        return <span className="icon-tile icon-tile-sm icon-tile-danger"><Axe className="w-3.5 h-3.5" /></span>;
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="modal-overlay z-50"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="modal-panel max-w-2xl max-h-[80vh]"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="link-item-title"
        >
          <div className="modal-header">
            <h2 id="link-item-title" className="modal-title">Link Item to Incident</h2>
            <button onClick={onClose} className="modal-close" title="Close" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value as 'all' | 'note' | 'warning' | 'ban')}
              className="glass-select sm:w-40"
              aria-label="Item type"
            >
              <option value="all">All Types</option>
              <option value="note">Notes</option>
              <option value="warning">Warnings</option>
              <option value="ban">Bans</option>
            </select>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="glass-input w-full pl-9"
                placeholder="Search by content, ID, or player name..."
                aria-label="Search items"
              />
            </div>
          </div>

          {error && (
            <div className="alert alert-danger mb-4" role="alert">
              <AlertCircle />
              <span>{error}</span>
            </div>
          )}

          <div className="modal-body">
            {loading && (
              <div className="space-y-2" aria-busy="true">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="subcard p-3 flex items-center gap-3">
                    <SkeletonLoader className="h-7 w-7 rounded-md" />
                    <div className="flex-1 space-y-2">
                      <SkeletonLoader className="h-3.5 w-1/3" />
                      <SkeletonLoader className="h-3 w-2/3" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!loading && searchQuery.length < 2 && (
              <EmptyState
                bare
                compact
                icon={Search}
                title="Enter at least 2 characters to search"
                description="Search notes, warnings and bans by content, ID or player name."
              />
            )}

            {!loading && searchQuery.length >= 2 && results.length === 0 && (
              <EmptyState
                bare
                compact
                icon={AlertCircle}
                title="No items found"
                description="Try a different search term or item type."
              />
            )}

            {!loading && results.length > 0 && (
              <div className="space-y-2">
                {results.map((item) => (
                  <motion.button
                    key={`${item.type}-${item.id}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    onClick={() => handleLink(item.id, item.type)}
                    disabled={isLinking === item.id}
                    className="w-full text-left subcard p-3 hover:border-glass-200/25 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center gap-3">
                      {getTypeIcon(item.type)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm text-white font-medium capitalize">{item.type}</span>
                          {item.user_uuid && (
                            <span className="text-glass-400 text-[11px] font-mono truncate">{item.user_uuid.substring(0, 8)}…</span>
                          )}
                        </div>
                        <p className="text-sm text-glass-300 truncate">{item.content || item.id}</p>
                      </div>
                      {isLinking === item.id && (
                        <Loader className="w-4 h-4 animate-spin text-accent-400" />
                      )}
                    </div>
                  </motion.button>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
